export enum UserObjectDimension {
  Knowledge = 'knowledge',
  Interest = 'interest',
  CognitiveStyle = 'cognitive_style',
  Motivation = 'motivation',
}

export const USER_OBJECT_DIMENSIONS: UserObjectDimension[] = [
  UserObjectDimension.Knowledge,
  UserObjectDimension.Interest,
  UserObjectDimension.CognitiveStyle,
  UserObjectDimension.Motivation,
];

/** Claims at or above this confidence count as "strong" on the profile snapshot. */
export const STRONG_CLAIM_CONFIDENCE = 0.6;

export const DIMENSION_LABELS: Record<UserObjectDimension, string> = {
  [UserObjectDimension.Knowledge]: 'Knowledge',
  [UserObjectDimension.Interest]: 'Interest & Passion',
  [UserObjectDimension.CognitiveStyle]: 'Cognitive Style',
  [UserObjectDimension.Motivation]: 'Motivation & Purpose',
};

export type ClaimProvenanceKind = 'extracted' | 'manual' | 'edited' | 'retracted';

export interface DimensionStrengthSummary {
  dimension: UserObjectDimension;
  label: string;
  strongClaimCount: number;
  totalActiveCount: number;
  strengthDots: number;
}

export interface ProfileSnapshotDto {
  userId: string;
  tenantId: string;
  profileMaturity: string | null;
  sessionCount: number;
  turnCount: number;
  dimensions: DimensionStrengthSummary[];
  topSignals: string[];
}

export interface ClaimSummaryDto {
  id: string;
  predicate: string;
  value: string;
  confidence: number;
  status: string;
  provenance: ClaimProvenanceKind;
  lastSeenTurn: number | null;
  dimension: UserObjectDimension;
}

export interface PredicateGroupDto {
  predicate: string;
  claims: ClaimSummaryDto[];
}

export interface DimensionViewDto {
  dimension: UserObjectDimension;
  label: string;
  description: string;
  strongClaimCount: number;
  activeByPredicate: PredicateGroupDto[];
  pendingByPredicate: PredicateGroupDto[];
}

export interface PipelineGateDto {
  name: string;
  passed: boolean;
  detail: string;
  logged: boolean;
}

export interface ConflictRecordDto {
  id: string;
  userId: string;
  tenantId: string;
  existingClaimId: string;
  candidateClaimId: string;
  reason: string;
  status: 'pending' | 'resolved';
}

export interface ClaimEvidenceDetailDto {
  quote: string;
  spanRole: string;
  conversationId: string;
  turnIds: string[];
}

export interface SourceTurnContextDto {
  chatId: string;
  turnId: string;
  turnIndex: number | null;
  userText: string | null;
  assistantText: string | null;
  contextTurns: Array<{
    role: 'before' | 'source' | 'after';
    turnIndex: number;
    userText: string;
    assistantText: string;
  }>;
}

export type ClaimLifeEventKind =
  | 'created'
  | 'reinforced'
  | 'weakened'
  | 'adjusted'
  | 'contradiction'
  | 'superseded'
  | 'scope_shift'
  | 'edited'
  | 'retracted';

export interface ClaimLifeEventDto {
  kind: ClaimLifeEventKind;
  timestamp: string;
  turnIndex: number | null;
  summary: string;
  confidenceDelta: number | null;
  confidenceAfter: number | null;
  detail: string | null;
}

export type ConfidenceTrajectoryEventKind = ClaimLifeEventKind | 'current';

export interface ConfidenceTrajectoryPointDto {
  timestamp: string;
  turnIndex: number | null;
  confidence: number;
  kind: ConfidenceTrajectoryEventKind;
  label: string;
}

export type CurationRetractionReason =
  | 'misattribution'
  | 'low_quality'
  | 'privacy_concern'
  | 'study_protocol_exclusion'
  | 'other';

export type ManualClaimSourceType =
  | 'intake_questionnaire'
  | 'pre_study_interview'
  | 'correction';

export interface RetractClaimRequestDto {
  reason: CurationRetractionReason;
  note?: string;
}

export interface CorrectClaimRequestDto {
  value?: string;
  confidence?: number;
  scope?: string[];
  key?: string;
  note?: string;
}

export interface AddManualClaimRequestDto {
  predicate: string;
  value: string;
  dimension: UserObjectDimension;
  confidence: number;
  scope?: string[];
  sourceType: ManualClaimSourceType;
  rationale: string;
}

export interface BulkRetractRequestDto {
  claimIds: string[];
  reason: CurationRetractionReason;
  note?: string;
}

export interface CurationResultDto {
  claimId: string;
  success: boolean;
  error?: string;
}

export interface PipelineExpandDto {
  writeSignalScore: number | null;
  gateConfidence: number | null;
  orchestratorSource: string | null;
  writeDecision: {
    shouldEnqueue: boolean;
    reason: string;
    maintenanceActions: string[];
    targetPredicates: string[];
  } | null;
  evaluationContextClaimCount: number;
  evaluationContextStrategies: string[];
  validationErrors: string[];
  conflictIds: string[];
  latencyMs: Record<string, number>;
  gateGuidance: Record<string, unknown> | null;
  contextRequest: Record<string, unknown> | null;
}

export interface ClaimEditHistoryEntryDto {
  id: string;
  eventType: string;
  createdAt: string;
  before: unknown;
  after: unknown;
  metadata: Record<string, unknown>;
}

export interface ClaimRelatedConflictDto {
  conflictId: string;
  status: 'pending' | 'resolved';
  reason: string;
  role: 'existing' | 'candidate';
  otherClaimId: string;
}

export interface ClaimDetailDto {
  claim: ClaimSummaryDto & { fullPayload: Record<string, unknown> };
  evidenceDetails: ClaimEvidenceDetailDto[];
  evidenceQuotes: string[];
  sourceTurnId: string | null;
  sourceContext: SourceTurnContextDto | null;
  lifeEvents: ClaimLifeEventDto[];
  confidenceTrajectory: ConfidenceTrajectoryPointDto[];
  relatedConflicts: ClaimRelatedConflictDto[];
  reinforcementTurns: number[];
  pipelineGates: PipelineGateDto[];
  pipelineExpand: PipelineExpandDto | null;
  writeTrace: Record<string, unknown> | null;
  editHistory: ClaimEditHistoryEntryDto[];
}

export type ConflictTypedAs =
  | 'explicit_replacement'
  | 'temporal_update'
  | 'contextual_coexistence'
  | 'extraction_ambiguity'
  | 'likely_misattribution'
  | 'no_conflict';

export type ConflictResolutionAction =
  | 'SUPERSEDE_OLD'
  | 'COEXIST_WITH_REKEY'
  | 'MERGE'
  | 'DISCARD_CANDIDATE'
  | 'ESCALATE_REVIEW';

export interface ConflictViewItemDto {
  conflict: ConflictRecordDto;
  existingClaim: ClaimSummaryDto | null;
  candidateClaim: ClaimSummaryDto | null;
  existingEvidence: string[];
  candidateEvidence: string[];
  existingSourceTurnId: string | null;
  candidateSourceTurnId: string | null;
  typedAs: ConflictTypedAs | null;
  resolutionAction: ConflictResolutionAction | null;
}

export interface MemoryTurnEventDto {
  kind: 'stored' | 'reinforced' | 'retrieved' | 'no_new_claims';
  claimId?: string;
  predicate?: string;
  value?: string;
  dimension?: UserObjectDimension;
  confidence?: number;
  delta?: number;
}

export interface ConversationTurnDto {
  turnIndex: number;
  turnId: string;
  userText: string;
  assistantText: string;
  timestamp: string;
  memoryEvents: MemoryTurnEventDto[];
  useSynapse: boolean;
  hasReadPathSnapshot: boolean;
}

export interface ReadPathScoreComponentsDto {
  relevance: number;
  confidence: number;
  scope: number;
  temporal: number;
  risk: number;
}

export interface ReadPathCandidateDto {
  objectId: string;
  bundleItemId: string;
  objectType: 'claim' | 'evidence' | 'episode' | 'entity';
  predicate: string | null;
  value: string | null;
  label: string;
  totalScore: number;
  rawScore: number;
  scoreComponents: ReadPathScoreComponentsDto;
  channels: string[];
  includedInFinalPool: boolean;
  includedInContextBundle: boolean;
  includedInInjectedContext: boolean;
  includedInUcb: boolean;
  dropReason: string | null;
}

export interface ReadPathLensDto {
  turnId: string;
  chatId: string;
  traceId: string | null;
  snapshotAvailable: boolean;
  ucbRenderedText: string | null;
  contextBundleRenderedText: string | null;
  injectedContextText: string | null;
  selectedCandidates: ReadPathCandidateDto[];
  droppedCandidates: ReadPathCandidateDto[];
  aggregateDrops: Record<string, number | boolean>;
  retrievalMiss: boolean;
}

export interface ConversationMemoryViewDto {
  chatId: string;
  title: string;
  personaId: string | null;
  turns: ConversationTurnDto[];
}
